import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity,
  Building2,
  BarChart3,
  FileCheck,
  FileText,
  Inbox,
  LayoutDashboard,
  Mail,
  MapPin,
  Menu,
  Paperclip,
  PanelLeft,
  PlayCircle,
  ReceiptText,
  Layers,
  // Settings,
  ShieldCheck,
  Users,
  Zap,
} from 'lucide-react';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { useIsMobile } from '../../../foundation/altzero_base/base/components/ui/use-mobile';
import { Sheet, SheetContent } from '../../../foundation/altzero_base/base/components/ui/sheet';
import { NewBadge } from '../components/NewBadge';

type NavItem = {
  name: string;
  href: string;
  icon: React.ElementType;
  disabled?: boolean;
  /** Being replaced by Bill Runs; still works until the business signs off. */
  retiring?: boolean;
  /** Recently added; tagged so the team notices it. */
  isNew?: boolean;
};

const navItems: NavItem[] = [
  { name: 'Dashboard',      href: '/bill-validation',                    icon: LayoutDashboard },
  { name: 'Entities',       href: '/bill-validation/organisations',       icon: Building2 },
  { name: 'Sites',          href: '/bill-validation/sites',               icon: MapPin },
  { name: 'Contacts',       href: '/bill-validation/contacts',            icon: Users },
  { name: 'Inbox',          href: '/bill-validation/inbox',               icon: Inbox },
  { name: 'Bill Runs',      href: '/bill-validation/runs',                icon: Layers, isNew: true },
  { name: 'Bills',          href: '/bill-validation/bills',               icon: ReceiptText, retiring: true },
  // Parse Status was here (job 75); it is now a Super Admin tab.
  { name: 'Validations',    href: '/bill-validation/validations',         icon: ShieldCheck,  disabled: true },
  { name: 'Attachments',    href: '/bill-validation/attachments',         icon: Paperclip },
  { name: 'Bulk Bill Run',  href: '/bill-validation/bill-runs',           icon: PlayCircle, retiring: true },
  { name: 'Bulk Customer Report', href: '/bill-validation/reports',             icon: FileText, retiring: true },
  { name: 'Reports',        href: '/bill-validation/report-hub',          icon: BarChart3, isNew: true },
  { name: 'Network Tariff Analysis', href: '/bill-validation/nta',        icon: Zap, isNew: true },
  { name: 'Email Templates', href: '/bill-validation/email-templates',    icon: Mail },
  { name: 'Jobs',           href: '/bill-validation/admin/jobs',          icon: Activity, isNew: true },
  { name: 'NEM Validator',  href: '/bill-validation/nem-validator',       icon: FileCheck },
  // { name: 'Admin',          href: '/bill-validation/admin',               icon: Settings },
];

type Props = {
  forceCollapsed?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
};

export default function BillValidationSidebar({
  forceCollapsed = false,
  mobileOpen = false,
  onMobileClose = () => {},
}: Props) {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(forceCollapsed);

  const isActive = (href: string) =>
    href === '/bill-validation'
      ? location.pathname === '/bill-validation'
      // Segment boundary, so /reports (Bulk Customer Report) never lights up for /report-hub.
      : location.pathname === href || location.pathname.startsWith(`${href}/`);

  const SidebarContent = () => (
    <>
      {/* Header */}
      <div className={cn('border-b border-gray-200 transition-all duration-300', collapsed ? 'p-3' : 'p-4')}>
        <div className={cn('flex items-center', collapsed ? 'flex-col gap-2' : 'justify-between')}>
          <Link
            to="/bill-validation"
            className={cn('flex items-center transition-all duration-300', collapsed ? 'justify-center' : 'space-x-2')}
            onClick={() => isMobile && onMobileClose()}
          >
            <FileCheck className="h-5 w-5 text-blue-600 shrink-0" />
            {!collapsed && (
              <div>
                <h1 className="text-base font-bold text-gray-900">Bill Validation</h1>
                <p className="text-xs text-gray-500">Energy bill analysis</p>
              </div>
            )}
          </Link>

          {!isMobile && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed
                ? <PanelLeft className="h-4 w-4 text-gray-600" />
                : <Menu className="h-4 w-4 text-gray-600" />}
            </button>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className={cn('flex-1 space-y-1 overflow-y-auto', collapsed ? 'p-2' : 'p-3')}>
        {navItems.map((item) =>
          item.disabled ? (
            <div
              key={item.href}
              title={collapsed ? `${item.name} (coming soon)` : undefined}
              className={cn(
                'flex items-center rounded-lg text-sm font-medium cursor-not-allowed select-none opacity-40',
                collapsed ? 'justify-center p-3 w-10 h-10 mx-auto' : 'px-3 py-2 gap-3',
                'text-gray-400',
              )}
            >
              <item.icon className={cn('shrink-0', collapsed ? 'h-5 w-5' : 'h-4 w-4')} />
              {!collapsed && (
                <span className="flex items-center gap-1.5">
                  {item.name}
                  <span className="text-[10px] font-normal bg-gray-100 text-gray-400 px-1 rounded">soon</span>
                </span>
              )}
            </div>
          ) : (
            <Link
              key={item.href}
              to={item.href}
              title={item.retiring ? `${item.name} — being replaced by Bill Runs` : collapsed ? item.name : undefined}
              onClick={() => isMobile && onMobileClose()}
              className={cn(
                'flex items-center rounded-lg text-sm font-medium transition-all duration-200',
                collapsed ? 'justify-center p-3 w-10 h-10 mx-auto' : 'px-3 py-2 gap-3',
                isActive(item.href)
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                  : 'text-gray-700 hover:bg-gray-100',
              )}
            >
              <item.icon className={cn('shrink-0', collapsed ? 'h-5 w-5' : 'h-4 w-4')} />
              {!collapsed && (item.retiring || item.isNew ? (
                <span className="flex items-center gap-1.5">
                  {item.name}
                  {item.retiring && <span className="text-[10px] font-normal bg-amber-50 text-amber-700 border border-amber-200 px-1 rounded whitespace-nowrap">to be deleted</span>}
                  {item.isNew && <NewBadge />}
                </span>
              ) : <span>{item.name}</span>)}
            </Link>
          )
        )}
      </nav>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={onMobileClose}>
        <SheetContent side="left" className="w-60 p-0">
          <div className="flex flex-col h-full bg-white">
            <SidebarContent />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className={cn(
      'hidden md:flex bg-white border-r border-gray-200 flex-col transition-all duration-300 ease-in-out sticky top-0 h-screen overflow-y-auto shrink-0',
      collapsed ? 'w-14' : 'w-56',
    )}>
      <SidebarContent />
    </div>
  );
}
